/**
 * AutoReminderSync — zero-UI component that syncs auto-reminders
 * whenever profile expiry dates change.
 *
 * Mount once inside ProfileProvider (e.g., alongside SessionTimeoutHandler).
 */

import { useEffect, useRef } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { autoReminderService } from '../services/autoReminderService';
import { pushNotificationService } from '../services/pushNotificationService';
import { showToast } from '../components/ui/Toast';
import { auth } from '../config/firebase';

const DEBOUNCE_MS = 3000;

export function AutoReminderSync() {
  const { currentProfile } = useProfile();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncedRef = useRef<string>('');
  const pushInitRef = useRef(false);

  // Sync auto-reminders when profile expiry dates change
  useEffect(() => {
    if (!currentProfile?.id) return;

    const fingerprint = JSON.stringify({
      ins: (currentProfile.insurancePolicies || []).map(p => ({ id: p.id, rd: p.renewalDate })),
      per: (currentProfile.personalItems || []).map(p => ({ id: p.id, ed: p.expiryDate })),
      veh: (currentProfile.vehicles || []).map(v => ({
        id: v.id,
        docs: (v.documents || []).filter(d => d.expiresAt).map(d => ({ id: d.id, ea: d.expiresAt })),
      })),
    });

    if (fingerprint === lastSyncedRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      lastSyncedRef.current = fingerprint;
      autoReminderService.syncReminders(currentProfile.id, currentProfile).catch(() => {});
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentProfile]);

  // Register push token + listen for foreground messages
  useEffect(() => {
    if (pushInitRef.current) return;
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    const prefs = autoReminderService.getPreferences();
    if (!prefs.enabled || !prefs.pushEnabled) return;
    if (!pushNotificationService.isSupported()) return;
    if (pushNotificationService.getPermissionState() !== 'granted') return;

    pushInitRef.current = true;
    pushNotificationService.registerToken(userId).catch(() => {});

    const unsubscribe = pushNotificationService.onForegroundMessage(({ title, body }) => {
      showToast.info(`${title}: ${body}`, 5000);
    });

    return unsubscribe;
  }, [currentProfile]);

  return null;
}
